'use client';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import Link from 'next/link';
import { type Salon } from '@/app/lib/salons';
import { slugify } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, Phone, Globe, ParkingCircle, Info, Lightbulb, Star, Sparkles } from 'lucide-react';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useEffect, useState, useCallback } from 'react';
import SalonReviews from './SalonReviews';
import ReviewForm from './ReviewForm';
import DaisyconAd from './DaisyconAd';
import { daisyconAds } from '@/app/lib/ads';

const BackLink = ({ salon }: { salon: Salon }) => {
  // The 'router.back()' logic was unreliable. This simplified approach always
  // navigates to the correct area overview page, providing a consistent UX.
  const fallbackHref = `/hondenkapsalons-in-${slugify(salon.province)}/${slugify(salon.area)}`;

  return (
    <Button
      asChild
      variant="outline"
      className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30 backdrop-blur-sm transition-colors"
    >
      <Link href={fallbackHref}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Terug naar overzicht
      </Link>
    </Button>
  );
};


const ensureUrlProtocol = (url: string) => {
  if (!url) return url;
  if (!/^(https?:\/\/)/i.test(url)) {
    return `https://${url}`;
  }
  return url;
};

const parseOpeningHours = (hoursString: string): { day: string; hours: string }[] => {
    if (!hoursString || typeof hoursString !== 'string') return [];

    const dayOrder = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
    const dayMap: { [key: string]: string } = {
        'ma': 'Maandag', 'di': 'Dinsdag', 'wo': 'Woensdag', 'do': 'Donderdag', 'vr': 'Vrijdag', 'za': 'Zaterdag', 'zo': 'Zondag',
        'maa': 'Maandag', 'din': 'Dinsdag', 'woe': 'Woensdag', 'don': 'Donderdag', 'vri': 'Vrijdag', 'zat': 'Zaterdag', 'zon': 'Zondag',
        'maandag': 'Maandag', 'dinsdag': 'Dinsdag', 'woensdag': 'Woensdag', 'donderdag': 'Donderdag', 'vrijdag': 'Vrijdag', 'zaterdag': 'Zaterdag', 'zondag': 'Zondag'
    };

    const result: { [key: string]: string } = {};

    // Standardize separators like newlines and semicolons.
    const entries = hoursString.replace(/\n/g, ';').split(';').map(s => s.trim()).filter(Boolean);

    for (const entry of entries) {
        let dayPart: string;
        let timePart: string;

        const parts = entry.split(/:(.*)/s); // Split on the first colon
        if (parts.length > 1 && parts[1].trim() !== '') { // Format is "Days: Times" and times are not empty
            dayPart = parts[0].trim().toLowerCase();
            timePart = parts[1].trim();
        } else { // No colon, or empty after colon. Might be "Days Status" like "Zondag gesloten"
            const normalizedEntry = entry.replace(':', ' ').toLowerCase();
            if (normalizedEntry.includes('gesloten')) {
                timePart = 'Gesloten';
                dayPart = normalizedEntry.replace('gesloten', '').trim();
            } else if (normalizedEntry.includes('op afspraak')) {
                timePart = 'Op afspraak';
                dayPart = normalizedEntry.replace('op afspraak', '').trim();
            } else {
                continue; // Unrecognized format
            }
        }
        
        // Normalize different range indicators (like t.e.m., tot, t/m) to a simple hyphen
        dayPart = dayPart.replace(/\s+(t\.e\.m\.?|t\/m|tot en met|tot)\s+/g, '-');

        // Handle various ways of listing days: "Ma, Di", "Ma-Vr", "Ma & Di"
        const daysToProcess = dayPart.replace(/\s+(en|&)\s+/gi, ',').split(',').map(d => d.trim()).filter(Boolean);

        for (const dayString of daysToProcess) {
            if (dayString.includes('-')) {
                // Handle ranges like "ma-vr"
                const [startDay, endDay] = dayString.split('-').map(d => d.trim());
                const canonicalStartDay = dayMap[startDay] || dayMap[startDay.substring(0, 3)] || dayMap[startDay.substring(0, 2)];
                const canonicalEndDay = dayMap[endDay] || dayMap[endDay.substring(0, 3)] || dayMap[endDay.substring(0, 2)];

                if (canonicalStartDay && canonicalEndDay) {
                    const startIndex = dayOrder.indexOf(canonicalStartDay);
                    const endIndex = dayOrder.indexOf(canonicalEndDay);
                    if (startIndex !== -1 && endIndex !== -1 && startIndex <= endIndex) {
                        for (let i = startIndex; i <= endIndex; i++) {
                            result[dayOrder[i]] = timePart;
                        }
                    }
                }
            } else {
                // Handle single days
                const canonicalDay = dayMap[dayString] || dayMap[dayString.substring(0, 3)] || dayMap[dayString.substring(0, 2)];
                if (canonicalDay) {
                    result[canonicalDay] = timePart;
                }
            }
        }
    }

    return dayOrder.map(day => ({
        day: day,
        hours: result[day] || 'Niet opgegeven'
    }));
};

export default function SalonDetailClient({ salon }: { salon: Salon }) {
  const openingHours = parseOpeningHours(salon.openingHours || '');
  const salonId = `${slugify(salon.province)}-${slugify(salon.area)}-${salon.slug}`;
  const [reviewStats, setReviewStats] = useState<{ score: number; count: number } | null>(null);
  const [shuffledAds, setShuffledAds] = useState<string[]>([]);

  useEffect(() => {
    // This effect runs on the client side to prevent hydration mismatch.
    // It shuffles the available ads to ensure the two ad slots on this page
    // do not show the same ad.
    const ads = [...daisyconAds.salonDetailAds];
    // Fisher-Yates shuffle algorithm
    for (let i = ads.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [ads[i], ads[j]] = [ads[j], ads[i]];
    }
    setShuffledAds(ads);
  }, []); // The empty dependency array ensures this runs only once on mount.


  const handleReviewsLoaded = useCallback((stats: { score: number; count: number }) => {
    setReviewStats(stats);
  }, []);

  const getDayAbbreviation = (day: string): string | null => {
    const map: { [key: string]: string } = {
        'Maandag': 'Mo', 'Dinsdag': 'Tu', 'Woensdag': 'We', 'Donderdag': 'Th', 'Vrijdag': 'Fr', 'Zaterdag': 'Sa', 'Zondag': 'Su'
    };
    return map[day] || null;
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'PetStore', // or ProfessionalService, PetStore is more specific
    name: salon.name,
    address: {
      '@type': 'PostalAddress',
      streetAddress: salon.address.split(',')[0],
      addressLocality: salon.area,
      addressRegion: salon.province,
      addressCountry: 'BE'
    },
    telephone: salon.phone ? salon.phone.replace(/\s/g, '') : undefined,
    url: salon.url && salon.url !== 'Geen officiële website gevonden' ? ensureUrlProtocol(salon.url) : undefined,
    image: salon.pagePicture,
    aggregateRating: reviewStats && reviewStats.count > 0 ? {
        '@type': 'AggregateRating',
        ratingValue: reviewStats.score.toFixed(1),
        reviewCount: reviewStats.count
    } : undefined,
    openingHoursSpecification: openingHours.map(({ day, hours }) => {
        const dayAbbr = getDayAbbreviation(day);
        if (!dayAbbr || hours.toLowerCase() === 'gesloten' || hours.toLowerCase() === 'niet opgegeven') return null;
        
        const times = hours.match(/(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/);
        if (!times) return null;

        return {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: `https://schema.org/${dayAbbr}`,
            opens: times[1],
            closes: times[2]
        };
    }).filter(Boolean),
  };

  const ClaimBanner = () => (
    <Card className="bg-primary/10 border-primary/30 mt-6">
      <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
        <div className="flex-shrink-0">
          <Info className="h-8 w-8 text-primary" />
        </div>
        <div className="flex-grow text-sm">
          <h3 className="font-bold">Eigenaar van dit kapsalon?</h3>
          <p className="text-muted-foreground">
            Claim deze pagina voor slechts €1/maand en personaliseer ze met foto's en extra informatie.
          </p>
        </div>
        <Button asChild className="w-full sm:w-auto mt-2 sm:mt-0">
          <Link href="/contact">
            Claim deze pagina
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
  
  const TipsCard = () => (
    <Card className="mt-6">
      <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
        <div className="flex-shrink-0">
          <Lightbulb className="h-8 w-8 text-primary" />
        </div>
        <div className="flex-grow text-sm">
          <h3 className="font-bold">Tips voor de trimbeurt</h3>
          <p className="text-muted-foreground">
            Een goede voorbereiding is het halve werk. Bekijk onze tips voor een stressvrije ervaring voor uw hond.
          </p>
        </div>
        <Button asChild className="w-full sm:w-auto mt-2 sm:mt-0" variant="outline">
          <Link href="/tips">
            Bekijk alle tips
          </Link>
        </Button>
      </CardContent>
    </Card>
  );

  const extraInfoRaw = (salon.extra || '').trim();
  const displayExtra = (extraInfoRaw.startsWith('1') || extraInfoRaw.startsWith('0')) ? extraInfoRaw.substring(1).trim() : extraInfoRaw;


  return (
    <div className="bg-muted min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="relative w-full h-[250px] bg-slate-800">
        {salon.pagePicture ? (
          <Image
            src={salon.pagePicture}
            alt={`Foto van ${salon.name}`}
            fill
            className="object-cover"
            priority
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-t from-primary/50 to-primary" />
        )}
        <div className="absolute inset-0 bg-black/50 p-4 sm:p-6 flex flex-col justify-between">
           <div className="flex justify-between items-start">
              <BackLink salon={salon} />
           </div>
           <div>
              <Link href={`/hondenkapsalons-in-${slugify(salon.province)}/${slugify(salon.area)}`}>
                <Badge variant="secondary" className="mb-2 bg-white/20 text-white border-none hover:bg-white/30 transition-colors text-sm px-3 py-1">{salon.area}</Badge>
              </Link>
              <div className="flex items-center gap-4 flex-wrap">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-md sm:text-5xl text-white">{salon.name}</h1>
                {reviewStats && reviewStats.count > 0 && (
                    <a href="#reviews-section" title={`Scroll naar recensies. Gemiddelde score: ${reviewStats.score.toFixed(1)} op basis van ${reviewStats.count} review(s).`} className="transition-transform hover:scale-105">
                        <div className="flex items-center gap-2 bg-amber-400 text-amber-900 font-bold px-3 py-1.5 rounded-full text-lg cursor-pointer">
                            <Star className="w-5 h-5 fill-current" />
                            <span>{reviewStats.score.toFixed(1)}</span>
                            <span className="text-sm font-normal text-amber-800">({reviewStats.count})</span>
                        </div>
                    </a>
                )}
              </div>
           </div>
        </div>
      </div>

      <main className="p-4 sm:p-6 max-w-4xl mx-auto pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-xl bg-card">
              <CardHeader>
                <CardTitle>Contact &amp; Adres</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                  <Button asChild variant="outline" className="w-full justify-start text-left h-auto">
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${salon.name}, ${salon.address}`)}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-start gap-4"
                    >
                      <Home className="text-muted-foreground mt-1"/>
                      <span className="flex-grow break-words whitespace-pre-wrap">{salon.address}</span>
                    </a>
                  </Button>
                  
                  {salon.phone &&
                    <Button asChild variant="outline" className="w-full justify-start">
                      <a href={`tel:${salon.phone.replace(/\s/g, '')}`} className="flex items-center gap-4">
                        <Phone className="text-muted-foreground"/>
                        {salon.phone}
                      </a>
                    </Button>
                  }
                  {salon.url && salon.url !== 'Geen officiële website gevonden' && (
                    <Button asChild variant="default" className="w-full justify-start">
                      <a href={ensureUrlProtocol(salon.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4">
                        <Globe />
                        Bezoek Website
                      </a>
                    </Button>
                  )}
              </CardContent>
            </Card>

            {(openingHours.length > 0 && openingHours.some(h => h.hours !== 'Niet opgegeven')) && (
              <Card className="shadow-xl bg-card">
                <CardHeader>
                  <CardTitle>Openingsuren</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                      <TableBody>
                          {openingHours.filter(h => h.hours !== 'Niet opgegeven').map(({day, hours}, index) => (
                              <TableRow key={`${day}-${index}`}>
                                  <TableCell className="font-medium pr-2 py-2">{day}</TableCell>
                                  <TableCell className="text-right text-muted-foreground py-2 whitespace-pre-wrap">{hours}</TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {salon.parking && (
              <Card className="shadow-xl bg-card">
                <CardHeader>
                  <CardTitle>Parking</CardTitle>
                </CardHeader>
                <CardContent className="flex items-start gap-3 text-sm">
                  <ParkingCircle className="h-4 w-4 mt-1 text-muted-foreground" />
                  <p className="text-muted-foreground whitespace-pre-wrap flex-grow">{salon.parking}</p>
                </CardContent>
              </Card>
            )}

            {salon.extra2 && (
              <Card className="shadow-xl bg-card">
                <CardHeader>
                  <CardTitle>Specialisatie</CardTitle>
                </CardHeader>
                <CardContent className="flex items-start gap-3 text-sm">
                  <Sparkles className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                  <p className="text-muted-foreground whitespace-pre-wrap flex-grow">{salon.extra2}</p>
                </CardContent>
              </Card>
            )}
            
            {displayExtra && (
                <Card className="shadow-xl bg-card">
                  <CardHeader>
                    <CardTitle>Extra Diensten</CardTitle>
                  </CardHeader>
                  <CardContent className="flex items-start gap-3 text-sm">
                    <p className="text-muted-foreground whitespace-pre-wrap flex-grow">{displayExtra}</p>
                  </CardContent>
                </Card>
            )}
        </div>
        
        <div className="mt-6">
          {shuffledAds.length > 0 && <DaisyconAd html={shuffledAds[0]} />}
        </div>

        <div id="reviews-section" className="mt-6 scroll-mt-24">
            <SalonReviews salonId={salonId} onReviewsLoaded={handleReviewsLoaded} />
        </div>

        <div className="mt-6 text-center">
            <ReviewForm salonId={salonId} province={salon.province} />
        </div>
        
        <div className="mt-6">
          {shuffledAds.length > 1 && <DaisyconAd html={shuffledAds[1]} />}
        </div>

        <TipsCard />
        <ClaimBanner />

      </main>
    </div>
  );
}
